import { useState } from 'react';
import { ButtonProps, Confirm, Message, Segment } from 'semantic-ui-react';
import { Link } from 'react-router';
import styled from '@emotion/styled';
import { Button } from '@britishcouncil/react-admin';
import { usePermissions } from '@britishcouncil/react-common';

import { getErrorMessage } from 'utils';
import { useFormContext } from './FormContext';

export const FormActions = ({
  customSubmitBtn,
  deleteBtnProps,
  deleteConfirmationMessage,
  backUrl = '..',
}: {
  customSubmitBtn?: React.ReactNode;
  deleteBtnProps?: ButtonProps;
  deleteConfirmationMessage?: string;
  backUrl?: string;
}) => {
  const { permissions } = usePermissions();
  const {
    mode,
    setMode,
    onCancel,
    isSaving,
    isDeleting,
    isRestoring,
    onDelete,
    onRestore,
    error,
    editPermissions,
    deletePermissions,
    restorePermissions,
    createPermissions,
  } = useFormContext();

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isRestoreConfirmOpen, setIsRestoreConfirmOpen] = useState(false);

  const handleCancel = () => {
    onCancel?.();
    setMode('read');
  };

  const handleEdit = () => {
    setMode('edit');
  };

  const handleDelete = () => {
    setIsConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    setIsConfirmOpen(false);
    onDelete?.();
  };

  const handleCancelDelete = () => {
    setIsConfirmOpen(false);
  };

  const handleRestore = () => {
    setIsRestoreConfirmOpen(true);
  };

  const handleConfirmRestore = () => {
    setIsRestoreConfirmOpen(false);
    onRestore?.();
  };

  const handleCancelRestore = () => {
    setIsRestoreConfirmOpen(false);
  };

  const hasEditPermission =
    !editPermissions || editPermissions.some((permission) => permissions.includes(permission));

  const hasDeletePermission =
    !deletePermissions || deletePermissions.some((permission) => permissions.includes(permission));

  const hasRestorePermission =
    !restorePermissions ||
    restorePermissions.some((permission) => permissions.includes(permission));

  const hasCreatePermission =
    !createPermissions || createPermissions.some((permission) => permissions.includes(permission));

  const isAnyActionInProgress = isSaving || isDeleting || isRestoring;
  const errorMessage = error ? getErrorMessage(error) : null;

  return (
    <>
      <Confirm
        open={isConfirmOpen}
        content={
          deleteConfirmationMessage ?? 'You are going to delete record. Do you want to continue?'
        }
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        cancelButton="Cancel"
        confirmButton="Confirm"
        data-testid="form-delete-confirm"
      />
      <Confirm
        open={isRestoreConfirmOpen}
        content="You are going to restore record. Do you want to continue?"
        onCancel={handleCancelRestore}
        onConfirm={handleConfirmRestore}
        cancelButton="Cancel"
        confirmButton="Confirm"
        data-testid="form-restore-confirm"
      />
      <Segment>
        {errorMessage && (
          <Message negative>
            <Message.Header>Error</Message.Header>
            <p>{errorMessage}</p>
          </Message>
        )}
        <S.ButtonSegment basic>
          {(mode === 'read' || mode === 'create') && (
            <Button
              type="button"
              as={Link}
              to={backUrl}
              relative="path"
              icon="arrow left"
              content="Back"
              secondary
            />
          )}
          {mode === 'edit' && (
            <Button type="button" onClick={handleCancel} secondary content="Cancel" />
          )}
          <div>
            {mode !== 'create' && mode !== 'edit' && onDelete && hasDeletePermission && (
              <Button
                type="button"
                onClick={handleDelete}
                color="red"
                icon="trash"
                content="Delete"
                loading={isDeleting}
                disabled={isAnyActionInProgress}
                {...deleteBtnProps}
              />
            )}
            {mode !== 'create' && mode !== 'edit' && onRestore && hasRestorePermission && (
              <Button
                type="button"
                onClick={handleRestore}
                primary
                icon="redo"
                content="Restore"
                loading={isRestoring}
                disabled={isAnyActionInProgress}
              />
            )}
            {customSubmitBtn ?? (
              <>
                {mode === 'edit' && (
                  <Button
                    type="submit"
                    primary
                    icon="save"
                    content="Save"
                    loading={isSaving}
                    disabled={isAnyActionInProgress}
                  />
                )}
                {mode === 'create' && hasCreatePermission && (
                  <Button
                    type="submit"
                    primary
                    icon="save"
                    content="Create"
                    loading={isSaving}
                    disabled={isAnyActionInProgress}
                  />
                )}
              </>
            )}
            {mode === 'read' && hasEditPermission && (
              <Button
                type="button"
                onClick={handleEdit}
                icon="pencil"
                content="Edit"
                primary
                disabled={isAnyActionInProgress}
              />
            )}
          </div>
        </S.ButtonSegment>
      </Segment>
    </>
  );
};

const S = {
  ButtonSegment: styled(Segment)`
    && {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0;
    }
  `,
};
